import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@foundation/components/ui/card";
import { Button } from "@foundation/components/ui/button";
import { Badge } from "@foundation/components/ui/badge";
import { ScrollArea } from "@foundation/components/ui/scroll-area";
import { Stepper } from "@foundation/components/stepper";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  X,
  FileText,
  AlertCircle,
  CheckCircle,
  RotateCcw,
  Eye,
  ChevronLeft,
  ChevronRight,
  Edit,
  Save,
  FileCheck,
  Undo,
} from "lucide-react";
import { useToast } from "@foundation/hooks/use-toast";
import { useUser } from "@foundation/contextapi/UserContext";
import ScopingAILayout from "../components/RevisrLayout";
import { Textarea } from "@foundation/components/ui/textarea";

interface Change {
  id: string;
  type: 'addition' | 'deletion' | 'modification';
  content: string;
  oldContent?: string;
  newContent?: string;
  lineNumber: number;
  section: string;
  status: 'pending' | 'accepted' | 'reverted';
  category: 'branding' | 'compliance' | 'formatting' | 'content';
}

export default function EditAndApprove() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useUser();

  const [changes, setChanges] = useState<Change[]>([]);
  const [documentContent, setDocumentContent] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [currentChangeIndex, setCurrentChangeIndex] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState("");
  const [reviewedCount, setReviewedCount] = useState(0);
  const [undoStack, setUndoStack] = useState<{ index: number, change: Change }[]>([]);
  const [workflowType, setWorkflowType] = useState<string>("");

  // Get workflow type on mount
  useEffect(() => {
    const workflow = sessionStorage.getItem('workflow_type');
    setWorkflowType(workflow || '');
  }, []);

  // Update current_step to 3 when page loads
  useEffect(() => {
    const updateStepOnLoad = async () => {
      const policyReviewId = sessionStorage.getItem('policy_review_id');
      const orgId = sessionStorage.getItem('orgId');

      if (policyReviewId && user?.id) {
        try {
          // Update step to 3 when entering this page
          await fetch(`/api/revisr/policy-review/${policyReviewId}/step`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'x-user-id': user.id,
              'x-org-id': orgId || '',
            },
            body: JSON.stringify({ current_step: 3 }),
          });
          console.log('✅ Updated current_step to 3 on page load');
        } catch (err) {
          console.warn('⚠️ Could not update step on page load:', err);
        }
      }
    };

    updateStepOnLoad();
  }, [user?.id]);

  useEffect(() => {
    const loadChanges = async () => {
      const policyReviewId = sessionStorage.getItem('policy_review_id');
      const orgId = sessionStorage.getItem('orgId');

      // Try to fetch from backend storage first
      if (policyReviewId && user?.id) {
        try {
          const response = await fetch(`/api/revisr/policy-review/${policyReviewId}/changes`, {
            headers: {
              'x-user-id': user.id,
              'x-org-id': orgId || '',
            },
          });

          if (response.ok) {
            const { changes: backendChanges } = await response.json();
            if (backendChanges && backendChanges.length > 0) {
              setChanges(backendChanges);
              const storedDocumentContent = sessionStorage.getItem('documentContent');
              setDocumentContent(storedDocumentContent || '');
              setIsLoading(false);

              const reviewed = backendChanges.filter((c: Change) => c.status !== 'pending').length;
              setReviewedCount(reviewed);
              console.log('✅ Loaded changes from backend storage');
              return;
            }
          }
        } catch (err) {
          console.warn('Could not fetch from backend, falling back to session storage:', err);
        }
      }

      // Fall back to session storage
      const storedChanges = sessionStorage.getItem('detectedChanges');
      const storedDocumentContent = sessionStorage.getItem('documentContent');

      if (storedChanges) {
        const parsedChanges = JSON.parse(storedChanges);
        setChanges(parsedChanges);
        setDocumentContent(storedDocumentContent || '');
        setIsLoading(false);

        const reviewed = parsedChanges.filter((c: Change) => c.status !== 'pending').length;
        setReviewedCount(reviewed);
      } else {
        toast({
          title: "No data found",
          description: "Please start from the Scan & Review page.",
          variant: "destructive",
        });
        navigate("/revisr/scan-review");
      }
    };

    loadChanges();
  }, [navigate, toast, user?.id]);

  const currentChange = changes[currentChangeIndex] || null;

  const handlePrevChange = () => {
    if (currentChangeIndex > 0) {
      setCurrentChangeIndex(currentChangeIndex - 1);
      setIsEditing(false);
    }
  };

  const handleNextChange = () => {
    if (currentChangeIndex < changes.length - 1) {
      setCurrentChangeIndex(currentChangeIndex + 1);
      setIsEditing(false);
    }
  };

  const handleApprove = () => {
    if (!currentChange) return;

    // Save to undo stack before changing
    setUndoStack([...undoStack, { index: currentChangeIndex, change: { ...currentChange } }]);

    const updatedChanges = [...changes];
    updatedChanges[currentChangeIndex] = {
      ...currentChange,
      status: 'accepted'
    };

    setChanges(updatedChanges);

    if (currentChange.status === 'pending') {
      setReviewedCount(reviewedCount + 1);
    }

    // Move to next change if available
    if (currentChangeIndex < changes.length - 1) {
      handleNextChange();
    }
  };

  const handleReject = () => {
    if (!currentChange) return;

    // Save to undo stack before changing
    setUndoStack([...undoStack, { index: currentChangeIndex, change: { ...currentChange } }]);

    const updatedChanges = [...changes];
    updatedChanges[currentChangeIndex] = {
      ...currentChange,
      status: 'reverted'
    };

    setChanges(updatedChanges);

    if (currentChange.status === 'pending') {
      setReviewedCount(reviewedCount + 1);
    }

    // Move to next change if available
    if (currentChangeIndex < changes.length - 1) {
      handleNextChange();
    }
  };

  const handleEdit = () => {
    if (!currentChange) return;

    if (isEditing) {
      // Save to undo stack before changing
      setUndoStack([...undoStack, { index: currentChangeIndex, change: { ...currentChange } }]);

      // Save the edited content
      const updatedChanges = [...changes];
      updatedChanges[currentChangeIndex] = {
        ...currentChange,
        newContent: editedContent,
        status: 'accepted'
      };

      setChanges(updatedChanges);
      setIsEditing(false);

      if (currentChange.status === 'pending') {
        setReviewedCount(reviewedCount + 1);
      }
    } else {
      // Start editing
      setEditedContent(currentChange.newContent || currentChange.content);
      setIsEditing(true);
    }
  };

  const handleUndo = () => {
    if (undoStack.length === 0) return;

    const lastAction = undoStack[undoStack.length - 1];
    const updatedChanges = [...changes];

    // Restore the previous state of the change
    updatedChanges[lastAction.index] = lastAction.change;

    // If we're undoing a status change from pending to something else, decrement the reviewed count
    if (lastAction.change.status === 'pending' &&
      changes[lastAction.index].status !== 'pending') {
      setReviewedCount(reviewedCount - 1);
    }

    // If we're undoing a status change from something else to pending, increment the reviewed count
    if (lastAction.change.status !== 'pending' &&
      changes[lastAction.index].status === 'pending') {
      setReviewedCount(reviewedCount + 1);
    }

    setChanges(updatedChanges);

    // If we're currently viewing a different change than the one we're undoing,
    // navigate to the change we just undid
    if (currentChangeIndex !== lastAction.index) {
      setCurrentChangeIndex(lastAction.index);
    }

    // Remove the action from the undo stack
    setUndoStack(undoStack.slice(0, -1));
  };

  const handleApplyAll = () => {
    // Save current state to undo stack
    setUndoStack([...undoStack, {
      index: -1, change: {
        id: 'bulk-action',
        type: 'modification',
        content: 'Bulk action',
        lineNumber: 0,
        section: '',
        status: 'pending',
        category: 'compliance'
      }
    }]);

    // Apply all pending changes as accepted
    const updatedChanges = changes.map(change => {
      if (change.status === 'pending') {
        return { ...change, status: 'accepted' as const };
      }
      return change;
    });

    setChanges(updatedChanges);
    setReviewedCount(changes.length);
  };

  const handleRevertAll = () => {
    // Save current state to undo stack
    setUndoStack([...undoStack, {
      index: -1, change: {
        id: 'bulk-action',
        type: 'modification',
        content: 'Bulk action',
        lineNumber: 0,
        section: '',
        status: 'pending',
        category: 'compliance'
      }
    }]);

    // Revert all pending changes
    const updatedChanges = changes.map(change => {
      if (change.status === 'pending') {
        return { ...change, status: 'reverted' as const };
      }
      return change;
    });

    setChanges(updatedChanges);
    setReviewedCount(changes.length);
  };

  const handleNext = async () => {
    if (reviewedCount < changes.length) {
      toast({
        title: "Review Incomplete",
        description: `Please review all ${changes.length} changes before proceeding.`,
        variant: "destructive",
      });
      return;
    }

    // Store the reviewed changes for the preview page (fallback)
    sessionStorage.setItem('reviewedChanges', JSON.stringify(changes));
    sessionStorage.setItem('finalDocumentContent', documentContent);

    // Save changes to backend storage (step already updated on page load)
    const policyReviewId = sessionStorage.getItem('policy_review_id');
    const orgId = sessionStorage.getItem('orgId');

    if (policyReviewId && user?.id) {
      try {
        // Update JSON changes with approved/rejected statuses
        await fetch(`/api/revisr/policy-review/${policyReviewId}/changes`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'x-user-id': user.id,
            'x-org-id': orgId || '',
          },
          body: JSON.stringify({ changes }),
        });

        console.log('✅ Saved reviewed changes to backend storage');
      } catch (err) {
        console.warn('⚠️ Could not save to backend:', err);
        // Continue even if this fails - session storage has the data
      }
    }

    // Navigate to the policy preview page with the document ID
    if (policyReviewId) {
      navigate(`/revisr/policies/generated?id=${policyReviewId}`);
    } else {
      // Fallback to old path if no ID (shouldn't happen in normal flow)
      navigate("/revisr/preview-export");
    }
  };

  if (isLoading) {
    return (
      <ScopingAILayout>
        <div className="min-h-screen bg-[#FCFBF4] flex items-center justify-center overflow-x-hidden">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading changes...</p>
          </div>
        </div>
      </ScopingAILayout>
    );
  }

  return (
    <ScopingAILayout>
      <div className="min-h-screen bg-[#FCFBF4] overflow-x-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10">
          {/* Header */}
          <div className="mb-6 md:mb-8">
            <div className="flex items-center space-x-4 mb-4">
              <Button
                variant="ghost"
                onClick={() => navigate("/revisr/scan-review")}
                className="flex items-center space-x-2"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Back to Scan & Review</span>
              </Button>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Review and Approve</h1>
            <p className="text-gray-600 mt-2">
              Side-by-side review of identified discrepancies - ready for approval
            </p>
          </div>

          {/* Audit Progress Stepper */}
          {workflowType && (
            <div className="mb-8 overflow-x-auto">
              <div className="min-w-[600px] sm:min-w-0">
                <Stepper
                  currentStep={workflowType === 'create_policy' ? 3 : 3}
                  steps={
                    workflowType === 'create_policy'
                      ? [
                        { label: "Upload Document", description: "Upload your document for analysis" },
                        { label: "Select Template", description: "Choose formatting template" },
                        { label: "Preview & Export", description: "Finalize and export" },
                      ]
                      : [
                        { label: "Upload Documents", description: "Upload policy and standard documents" },
                        { label: "Scan & Review", description: "AI scans and identifies compliance gaps" },
                        { label: "Edit & Approve", description: "Review and approve changes" },
                        { label: "Preview & Export", description: "Finalize and export" },
                      ]
                  }
                />
              </div>
            </div>
          )}

          {/* Main Content - Diff Viewer */}
          {currentChange ? (
            <Card className="bg-white rounded-lg shadow-md mb-8 border-[#33b864]/20">
              <CardHeader className="border-b pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center space-x-2 text-lg">
                    <FileText className="h-5 w-5" />
                    <span>{currentChange.section}</span>
                  </CardTitle>
                </div>
                {/* <p className="text-sm text-gray-600 mt-1">{currentChange.content}</p> */}
              </CardHeader>
              <CardContent className="p-4 md:p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  {/* Left side - Old version */}
                  <div className="space-y-2">
                    <h3 className="text-sm font-medium text-gray-700 flex items-center">
                      <span className="inline-block w-3 h-3 rounded-full bg-red-500 mr-2"></span>
                      Old Version
                    </h3>
                    <div className="border rounded-md p-4 bg-red-50 min-h-[100px]">
                      {currentChange.oldContent ? (
                        <p className="text-sm whitespace-pre-wrap">{currentChange.oldContent}</p>
                      ) : (
                        <p className="text-sm text-gray-500 italic">No previous content</p>
                      )}
                    </div>
                  </div>

                  {/* Right side - New version */}
                  <div className="space-y-2">
                    <h3 className="text-sm font-medium text-gray-700 flex items-center">
                      <span className="inline-block w-3 h-3 rounded-full bg-green-500 mr-2"></span>
                      New Version
                    </h3>
                    {isEditing ? (
                      <Textarea
                        value={editedContent}
                        onChange={(e) => setEditedContent(e.target.value)}
                        className="min-h-[100px] border-green-200 bg-green-50"
                      />
                    ) : (
                      <div className="border rounded-md p-4 bg-green-50 min-h-[100px]">
                        {currentChange.newContent ? (
                          <p className="text-sm whitespace-pre-wrap">{currentChange.newContent}</p>
                        ) : (
                          <p className="text-sm whitespace-pre-wrap">{currentChange.content}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Navigation and Action buttons */}
                <div className="flex flex-wrap justify-center items-center mt-6 gap-2">
                  <Button
                    onClick={handlePrevChange}
                    disabled={currentChangeIndex === 0}
                    variant="outline"
                    className="gap-1"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>

                  <Button
                    onClick={handleReject}
                    variant="outline"
                    className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                    disabled={isEditing}
                  >
                    <X className="h-4 w-4 mr-1" />
                    Reject
                  </Button>

                  <Button
                    onClick={handleEdit}
                    variant="outline"
                    className="border-blue-200 text-blue-600 hover:bg-blue-50 hover:text-blue-700"
                  >
                    {isEditing ? (
                      <>
                        <Save className="h-4 w-4 mr-1" />
                        Save Edit
                      </>
                    ) : (
                      <>
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </>
                    )}
                  </Button>

                  <Button
                    onClick={handleApprove}
                    className="bg-[#33b864] hover:bg-[#33b864]/90 text-white"
                    disabled={isEditing}
                  >
                    <Check className="h-4 w-4 mr-1" />
                    Approve
                  </Button>

                  <Button
                    onClick={handleNextChange}
                    disabled={currentChangeIndex === changes.length - 1}
                    variant="outline"
                    className="gap-1"
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>

                  <Button
                    onClick={handleUndo}
                    disabled={undoStack.length === 0}
                    variant="ghost"
                    className="ml-2"
                  >
                    <Undo className="h-4 w-4 mr-1" />
                    Undo
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-white rounded-lg shadow-sm mb-8 border-[#33b864]/20 p-8 text-center">
              <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Changes to Review</h3>
              <p className="text-sm text-gray-500 max-w-md mx-auto">
                No discrepancies were found between the documents or all changes have been reviewed.
              </p>
            </Card>
          )}

          {/* Bottom toolbar */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 py-4 border-t">
            <div className="text-sm text-gray-600">
              <span className="font-medium">{reviewedCount}</span> of <span className="font-medium">{changes.length}</span> discrepancies reviewed
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                onClick={handleRevertAll}
                disabled={reviewedCount === changes.length}
                className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 gap-2 flex-1 sm:flex-none justify-center min-h-[44px]"
                variant="outline"
              >
                <RotateCcw className="h-4 w-4" />
                Revert All
              </Button>

              <Button
                onClick={handleApplyAll}
                disabled={reviewedCount === changes.length}
                className="gap-2 flex-1 sm:flex-none justify-center min-h-[44px]"
              >
                <FileCheck className="h-4 w-4" />
                Apply All
              </Button>

              <Button
                onClick={handleNext}
                disabled={reviewedCount < changes.length}
                className="bg-primary text-primary-foreground hover:bg-primary/90 gap-2 w-full sm:w-auto px-6 py-3 font-medium shadow-lg hover:shadow-xl transition-all duration-200 min-h-[44px]"
              >
                <ArrowRight className="h-4 w-4" />
                Finalise and Export
              </Button>
            </div>
          </div>
        </div>
      </div>
    </ScopingAILayout>
  );
} 